export type VolumeChannel = 'master' | 'music' | 'effect';

export interface Dial {
  channel: VolumeChannel;
  label: string;
  /** Diameter and ring thickness, in px. */
  size: number;
  stroke: number;
  color: string;
  /** Offset from the bottom-right corner of the overlay, in px. */
  right: number;
  bottom: number;
}

/**
 * The layout is declared once and used for both drawing the dials and deciding which one the
 * pointer is over - two copies of these numbers would drift, and a dial you cannot hit is worse
 * than no dial.
 *
 * Master is the big one in the corner because it is what a scroll anywhere else adjusts.
 */
export const DIALS: Dial[] = [
  { channel: 'master', label: 'master', size: 180, stroke: 16, color: '#00eaff', right: 0, bottom: 0 },
  { channel: 'effect', label: 'effects', size: 110, stroke: 8, color: '#7cfaff', right: 220, bottom: 120 },
  { channel: 'music', label: 'music', size: 110, stroke: 8, color: '#ffb3fa', right: 100, bottom: 220 },
];

/** How far outside a dial still counts as being on it, in px. */
const HIT_SLOP = 8;

/**
 * Gap between the overlay and the corner of the screen, in px. Shared with the CSS below, because
 * the dial offsets above are relative to the overlay's box rather than to the viewport - leaving it
 * out of the hit test puts every hit-box a full gap away from the dial it belongs to.
 */
export const OVERLAY_INSET = 32;

/** Which dial the pointer is over, or `master` - a scroll anywhere else is a master adjustment. */
export const dialAtPointer = (x: number, y: number, viewport: { width: number; height: number }): VolumeChannel => {
  for (const dial of DIALS) {
    if (dial.channel === 'master') continue;

    const left = viewport.width - OVERLAY_INSET - dial.right - dial.size;
    const top = viewport.height - OVERLAY_INSET - dial.bottom - dial.size;

    const isOver =
      x > left - HIT_SLOP && x < left + dial.size + HIT_SLOP && y > top - HIT_SLOP && y < top + dial.size + HIT_SLOP;

    if (isOver) return dial.channel;
  }

  return 'master';
};
